<template>
  <div
    v-if="displayedArticles.length > 0 || isLoading"
    class="section__articles-container"
  >
    <div>
      <component
        v-if="title && (displayedArticles.length > 0 || isLoading)"
        class="h4"
        :is="headingElement"
      >
        {{ title }}
      </component>
    </div>
    <div
      v-for="(article, index) in displayedArticles"
      :key="`related_article_${article.id}`"
      class="article-wrapper"
    >
      <DefaultArticlesArticleCard
        :article="article"
        :data-index="index"
        :type="ArticleComponentType.RECOMMENDED"
        :hide-author="hideAuthor"
        :hide-magazine="hideMagazine"
        :show-magazine-name="showMagazineName"
      />
    </div>

    <!-- Loading skeleton -->
    <div
      v-if="isLoading"
      v-for="item in articlesCount"
      :key="`related_skeleton_${item}`"
      class="article-wrapper"
    >
      <DefaultSkeletonsArticleCard :type="ArticleComponentType.RECOMMENDED" />
    </div>

    <!-- Load more -->
    <div
      v-if="hasMore && !isLoading"
      class="button-more-container"
    >
      <DefaultArticlesButtonMore
        :label="$t('articles.button_more')"
        :disabled="isLoadingMore"
        @click="loadMore"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ARTICLE_QUERY_TYPE, useArticlesStore } from '~/stores/useArticlesStore'
import { ArticleStoreType } from '~/libs/constants/ArticleStoreType'
import ArticleComponentType from '~/libs/constants/ArticleComponentType'
const props = withDefaults(
  defineProps<{
    title?: string
    headingElement?: string
    articlesCount?: number
    excludeArticleId?: string
    excludeArticlePath?: string
    hideAuthor?: boolean
    hideMagazine?: boolean
    showMagazineName?: boolean
  }>(),
  {
    headingElement: 'h3',
    articlesCount: 5,
    hideAuthor: true,
    hideMagazine: false,
    showMagazineName: true,
  }
)

const page = ref(1)
const isLoadingMore = ref(false)

const homepageStore = useArticlesStore(ArticleStoreType.HOMEPAGE, 'related')

const isLoading = computed(() => homepageStore.isLoading)

const filteredArticles = computed(() => {
  const articles = homepageStore.getOnlyFilteredArticles(0, 500)
  return articles.filter((article) => {
    if (props.excludeArticleId && article.id === props.excludeArticleId) return false
    if (props.excludeArticlePath && article.path) {
      const normalizedPath = article.path.replace(/^\/|\/$/g, '')
      const normalizedExclude = props.excludeArticlePath.replace(/^\/|\/$/g, '')
      if (normalizedPath === normalizedExclude) return false
    }
    return true
  })
})

const displayedArticles = computed(() => {
  const limit = props.articlesCount * page.value
  return filteredArticles.value.slice(0, limit)
})

const hasMore = computed(() => {
  const displayedCount = props.articlesCount * page.value
  return filteredArticles.value.length > displayedCount
})

const fetchArticles = async () => {
  const neededCount = props.articlesCount * page.value + 5
  const fetchLimit = Math.max(neededCount, 10)
  homepageStore.setLimit(fetchLimit)
  await homepageStore.fetchArticles(undefined, ARTICLE_QUERY_TYPE.WITH_AUTHOR)
}

const loadMore = async () => {
  isLoadingMore.value = true
  page.value += 1
  const neededCount = props.articlesCount * page.value + 5
  const fetchLimit = Math.max(neededCount, 10)
  if (homepageStore.articles.length < fetchLimit) {
    homepageStore.setLimit(fetchLimit)
    await homepageStore.fetchArticles(undefined, ARTICLE_QUERY_TYPE.WITH_AUTHOR)
  }
  isLoadingMore.value = false
}

onMounted(() => {
  fetchArticles()
})
</script>
